import React from "react";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import { keyBy } from "lodash";

const THUMBNAIL_WIDTH = 288;
const THUMBNAIL_HEIGHT = 162;
const MARK_SIZE = 5;
const MARK_AFTER_DIMENSIONS = { height: 8, width: 10 };

const SlideProgress = ({
  sections,
  currSlideInfo,
  numOfSlides,
  slidesMarks,
  updateSlideChangeInfo,
  slideNumsToHideSlideDisplay,
  thumbnailImages,
  isGamePlaying,
  shouldHideSlideDisplay,
}) => {
  const { color, slideNumber } = currSlideInfo;

  const slidesLabel = keyBy(slidesMarks, "value");

  const gradients = [];
  for (let i = 0; i < sections.length; i++) {
    const { color, cummulativeSectionLength } = sections[i];

    if (i === sections.length - 1) {
      gradients.push(
        `${color} ${getSectionColorCalcString(
          cummulativeSectionLength,
          2
        )} 100%`
      );
    } else {
      const nextCummulativeSectionLength =
        sections[i + 1].cummulativeSectionLength;
      gradients.push(
        `${color} ${getSectionColorCalcString(
          cummulativeSectionLength,
          // first section doesn't need offset at the start
          i === 0 ? 0 : 2
        )} ${getSectionColorCalcString(
          nextCummulativeSectionLength,
          -2
        )}, #18181b ${getSectionColorCalcString(
          nextCummulativeSectionLength,
          -2
        )} ${getSectionColorCalcString(nextCummulativeSectionLength, 2)}`
      );
    }
  }

  const themeObj = {};
  for (const section in sections) {
    const { color } = sections[section];
    themeObj[color] = { main: color };
  }

  const theme = createTheme({
    palette: themeObj,
  });

  const CustomSlider = styled(Slider)(({ defaultValue }) => {
    const progress = `${((defaultValue - 1) / (numOfSlides - 1)) * 100}%`;
    return {
      "& .MuiSlider-rail": {
        backgroundImage: `linear-gradient(to right, ${gradients.join(",")})`,
        maskImage: `linear-gradient(to right, black 0% ${progress}, rgb(0,0,0, .4) ${progress} 100%)`,
        height: 8,
        opacity: 1,
        borderRadius: 8,
      },
      "& .MuiSlider-thumb": {
        color: "white",
        height: 20,
        width: 20,
        border: "1.5px solid gray",
        boxShadow: "none !important",
        "&:before": {
          display: "none",
        },
        "&:hover": {
          cursor: "grab",
        },
        "&:active": {
          cursor: "grabbing",
        },
        "&:after": {
          width: "30px",
          height: "30px",
        },
      },
      "& .MuiSlider-mark": {
        // extend hit area of marks by adding a transparent div that is wider than the mark
        // to watch out for - extended hit area may cause user to go to wrong slide depending on where they clicked when tooltip is shown
        " &:after": {
          content: `""`,
          position: "absolute",
          top: `-${(MARK_AFTER_DIMENSIONS.height - MARK_SIZE) / 2}px`,
          left: `-${(MARK_AFTER_DIMENSIONS.width - MARK_SIZE) / 2}px`,
          width: `${MARK_AFTER_DIMENSIONS.width}px`,
          height: `${MARK_AFTER_DIMENSIONS.height}px`,
        },
      },
    };
  });

  const CustomMark = (props) => {
    const { value, color } = slidesMarks[props["data-index"]];
    const activeMark = value <= slideNumber;

    const mark = (
      <div
        className={props.className}
        style={{
          position: "absolute",
          height: MARK_SIZE,
          width: MARK_SIZE,
          borderRadius: "50%",
          background: activeMark ? "black" : color,
          bottom: "-0.5px",
          left: `calc(${props.style.left} - ${MARK_SIZE / 2}px)`,
        }}
      />
    );
    return <ThumbnailTooltip index={value} children={mark} />;
  };

  const ThumbnailTooltip = ({ index, children, open }) => {
    const text = slidesLabel[index]?.text;
    const caption = text ? `${index}: ${text}` : index;
    const tooltipTitle = (
      <Stack>
        {thumbnailImages && (
          <div
            style={{
              padding: 0,
              width: THUMBNAIL_WIDTH,
              height: THUMBNAIL_HEIGHT,
              backgroundImage: `url(${thumbnailImages})`,
              backgroundPositionX: `${-(index - 1) * THUMBNAIL_WIDTH}px`,
            }}
            alt="thumbnail"
          />
        )}
        <p className="thumbnail-caption">{caption}</p>
      </Stack>
    );

    return (
      // https://mui.com/material-ui/react-tooltip/#controlled-tooltips
      // Tooltip open - determined if tooltip should be shown or not
      // Slider's valueLabelDisplay properties - https://github.com/mui/material-ui/blob/439c1d5b8265ab85a3505e75654c63e7ddc7a743/packages/mui-material/src/Slider/SliderValueLabel.types.ts#L1
      // while not using the hook, more info on value label display's property https://mui.com/base-ui/react-slider/hooks-api/#use-slider
      // if props.open exist than value label is being displayed.

      <Tooltip
        title={tooltipTitle}
        placement="top"
        arrow
        open={open}
        slotProps={{
          popper: {
            // allows the tooltip to be seen in fullscreen mode
            disablePortal: true,
            // https://mui.com/material-ui/react-popper/
            //https://popper.js.org/docs/v2/modifiers/prevent-overflow/
            sx: {
              "& .MuiTooltip-tooltip": {
                padding: "4px",
              },
              pointerEvents: "none",
            },
            modifiers: [
              {
                name: "preventOverflow",
                enabled: true,
                options: {
                  tether: false,
                },
              },
            ],
          },
        }}
      >
        {children}
      </Tooltip>
    );
  };

  return (
    <div className={`progress-bars ${isGamePlaying ? "hide" : ""}`}>
      <ThemeProvider theme={theme}>
        <CustomSlider
          aria-label="Slides progress"
          color={color}
          defaultValue={slideNumber} // use defaultValue instead of value for thumb sliding
          min={1}
          max={numOfSlides}
          marks={slidesMarks}
          step={1}
          track={false}
          sx={{ padding: 0 }}
          onChangeCommitted={(_event, value) => {
            updateSlideChangeInfo({
              hideSlideDisplay:
                slideNumsToHideSlideDisplay.includes(value) ||
                shouldHideSlideDisplay(currSlideInfo.label),
              prevHiderSlideLabel:
                shouldHideSlideDisplay(currSlideInfo.label) &&
                currSlideInfo.label,
              newSlideNumber: value,
            });
          }}
          slots={{
            mark: CustomMark,
            valueLabel: (props) => (
              <ThumbnailTooltip
                index={props.value}
                children={props.children}
                open={props.open}
              />
            ),
          }}
          valueLabelDisplay="auto"
          tabIndex={-1}
        />
      </ThemeProvider>
    </div>
  );
};

function getSectionColorCalcString(cummulativeSectionLength, pixelOffset = 0) {
  return `calc(${cummulativeSectionLength * 100}% + ${pixelOffset}px)`;
}

export default SlideProgress;
